import React from 'react';
import {
  fetchNewsList, 
  fetchNewsById,
  fetchRelatedNews,
  fetchNewsCategories,
  mockNewsItems,
  mockNewsDetails
} from '../services/news-service';

/**
 * Hook for fetching news list with pagination and filtering
 * 
 * @param {number} page - Current page number
 * @param {number} limit - Number of items per page
 * @param {string} tag - Optional tag/category filter
 * @returns {Object} News items, pagination data, loading state, and error state
 */
export const useNewsList = (page = 1, limit = 9, tag = null) => {
  const [newsData, setNewsData] = React.useState({
    items: [],
    pagination: {
      total: 0,
      page: 1,
      limit: limit,
      pages: 1
    }
  });
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [isApiError, setIsApiError] = React.useState(false);

  const loadNewsList = React.useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setIsApiError(false);
    
    // Add a small artificial delay to ensure the UI shows the loading state
    await new Promise(resolve => setTimeout(resolve, 500));
    
    try {
      const data = await fetchNewsList(page, limit, tag);
      console.log('News data received:', data); // Debug log
      setNewsData(data);
      
      // Check if this is fallback data from an API error
      if (data.isErrorFallback) {
        setIsApiError(true);
      }
    } catch (err) {
      console.error('Error fetching news:', err); // Debug log
      setError(err instanceof Error ? err : new Error('Failed to fetch news list'));
      
      // Use mock data as fallback
      let filteredNews = tag 
        ? mockNewsItems.filter(item => item.category === tag)
        : mockNewsItems;
      
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedNews = filteredNews.slice(startIndex, endIndex);
      
      console.log('Using mock data fallback with items:', paginatedNews.length); // Debug log
      
      setNewsData({
        items: paginatedNews,
        pagination: {
          total: filteredNews.length,
          page,
          limit,
          pages: Math.ceil(filteredNews.length / limit)
        }
      });
      
      setIsApiError(true);
    } finally {
      setIsLoading(false);
    }
  }, [page, limit, tag]);

  React.useEffect(() => {
    loadNewsList();
  }, [loadNewsList]);

  return {
    news: newsData.items,
    pagination: newsData.pagination,
    isLoading,
    error,
    isApiError,
    refetch: loadNewsList
  };
};

/**
 * Hook to fetch news categories (id, name, code, description)
 */
export const useNewsCategories = () => {
  const [categories, setCategories] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const loadCategories = React.useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchNewsCategories();
      setCategories(data || []);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch categories'));
      // Fallback: derive categories from mockNewsItems
      const unique = [];
      mockNewsItems.forEach(item => {
        if (item.category && !unique.find(u => u.name === item.category)) {
          unique.push({ id: null, name: item.category, code: item.category.toLowerCase(), description: '' });
        }
      });
      setCategories(unique);
    } finally {
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  return { categories, isLoading, error, refetch: loadCategories };
};

/**
 * Hook for fetching a single news item by ID
 * 
 * @param {number|string} newsId - The news item ID
 * @returns {Object} The news item, loading state, and error state
 */
export const useNewsDetail = (newsId) => {
  const [newsDetail, setNewsDetail] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [isApiError, setIsApiError] = React.useState(false);

  const loadNewsDetail = React.useCallback(async () => {
    if (!newsId) {
      setNewsDetail(null);
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    setIsApiError(false);
    
    try {
      const data = await fetchNewsById(newsId);
      setNewsDetail(data);
      
      // Check if this is fallback data from an API error
      if (data.isErrorFallback) {
        setIsApiError(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error(`Failed to fetch news with ID: ${newsId}`));
      
      // Try to use mock data as fallback
      const newsItem = mockNewsItems.find(item => item.id === Number(newsId));
      if (newsItem) {
        const detailedNews = mockNewsDetails[newsId] || {
          ...newsItem,
          content: [newsItem.excerpt],
          author: "Команда AROMA",
          relatedNews: []
        };
        
        setNewsDetail(detailedNews);
        setIsApiError(true);
      }
    } finally {
      setIsLoading(false);
    }
  }, [newsId]);

  React.useEffect(() => {
    loadNewsDetail();
  }, [loadNewsDetail]);

  return { newsDetail, isLoading, error, isApiError, refetch: loadNewsDetail };
};

/**
 * Hook for fetching related news items
 * 
 * @param {number|string} newsId - The original news item ID
 * @param {number} limit - Maximum number of related items
 * @returns {Object} Related news items, loading state, and error state
 */
export const useRelatedNews = (newsId, limit = 2) => {
  const [relatedNews, setRelatedNews] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [isApiError, setIsApiError] = React.useState(false);

  const loadRelatedNews = React.useCallback(async () => {
    if (!newsId) {
      setRelatedNews([]);
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    setIsApiError(false);
    
    try {
      const data = await fetchRelatedNews(newsId, limit);
      setRelatedNews(data);
      
      // For related news we don't have a direct isErrorFallback flag,
      // so we'll need to determine if it's a fallback based on API integration
      setIsApiError(false); // Reset in case API is working
    } catch (err) {
      setError(err instanceof Error ? err : new Error(`Failed to fetch related news for ID: ${newsId}`));
      
      // Use mock data as fallback
      const newsItem = mockNewsItems.find(item => item.id === Number(newsId));
      if (newsItem) {
        const related = mockNewsItems
          .filter(item => item.category === newsItem.category && item.id !== Number(newsId))
          .slice(0, limit);
        
        setRelatedNews(related);
      } else {
        setRelatedNews([]);
      }
      
      setIsApiError(true);
    } finally {
      setIsLoading(false);
    }
  }, [newsId, limit]);

  React.useEffect(() => {
    loadRelatedNews();
  }, [loadRelatedNews]);

  return { relatedNews, isLoading, error, isApiError, refetch: loadRelatedNews };
};

/**
 * Hook for fetching featured news items for homepage or other sections
 * 
 * @param {number} limit - Maximum number of featured news items to retrieve
 * @returns {Object} Featured news items, loading state, and error state
 */
export const useFeaturedNews = (limit = 3) => {
  const [featuredNews, setFeaturedNews] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [isApiError, setIsApiError] = React.useState(false);

  const loadFeaturedNews = React.useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setIsApiError(false);
    
    try {
      // For featured news, we'll use the standard news list endpoint
      // In a real API, there might be a dedicated featured news endpoint
      const data = await fetchNewsList(1, limit);
      
      setFeaturedNews(data.items);
      
      // Check if this is fallback data from an API error
      if (data.isErrorFallback) {
        setIsApiError(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch featured news'));
      
      // Use mock data as fallback - the most recent items
      const featured = [...mockNewsItems]
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .slice(0, limit);
      
      setFeaturedNews(featured);
      setIsApiError(true);
    } finally {
      setIsLoading(false);
    }
  }, [limit]);

  React.useEffect(() => {
    loadFeaturedNews();
  }, [loadFeaturedNews]);

  return { featuredNews, isLoading, error, isApiError, refetch: loadFeaturedNews };
};